"use client";

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { AlertTriangle, X } from "lucide-react";
import { useTheme } from "../../utils/useTheme";

// ── Context ────────────────────────────────────────────────────────────────────

const ConfirmContext = createContext(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function ConfirmProvider({ children }) {
	const [state, setState] = useState(null); // { title, message, resolve }

	const confirm = useCallback(({ title, message } = {}) => {
		return new Promise((resolve) => {
			setState({ title, message, resolve });
		});
	}, []);

	const handleClose = (result) => {
		state?.resolve(result);
		setState(null);
	};

	return (
		<ConfirmContext.Provider value={confirm}>
			{children}
			{state && (
				<ConfirmDialog
					title={state.title}
					message={state.message}
					onConfirm={() => handleClose(true)}
					onCancel={() => handleClose(false)}
				/>
			)}
		</ConfirmContext.Provider>
	);
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useConfirm() {
	const ctx = useContext(ConfirmContext);
	if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
	return ctx;
}

// ── Dialog ─────────────────────────────────────────────────────────────────────

function ConfirmDialog({ title, message, onConfirm, onCancel }) {
	const { colors } = useTheme();
	const cancelRef = useRef(null);

	// Focus cancel button on mount for safe default
	useEffect(() => {
		cancelRef.current?.focus();
	}, []);

	// Keyboard handling
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape") onCancel();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onCancel]);

	return (
		<div
			className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
			onClick={onCancel}
		>
			<div
				className={`${colors.card} rounded-xl shadow-xl w-full max-w-sm`}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div className={`flex items-start justify-between p-5 border-b ${colors.border}`}>
					<div className="flex items-center gap-3">
						<div className="flex-shrink-0 w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
							<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
						</div>
						<h2 className={`text-base font-semibold ${colors.foreground}`}>
							{title ?? "¿Estás seguro?"}
						</h2>
					</div>
					<button
						onClick={onCancel}
						className={`p-1 rounded-xl ${colors.hoverSecondary} transition-colors`}
					>
						<X className={`w-4 h-4 ${colors.mutedForeground}`} />
					</button>
				</div>

				{/* Body */}
				{message && (
					<div className="px-5 py-4">
						<p className={`text-sm ${colors.mutedForeground}`}>{message}</p>
					</div>
				)}

				{/* Footer */}
				<div className={`flex items-center justify-end gap-3 px-5 py-4 border-t ${colors.border}`}>
					<button
						ref={cancelRef}
						onClick={onCancel}
						className={`px-4 py-2 text-sm font-medium ${colors.textSecondary} ${colors.secondary} ${colors.hoverSecondary} rounded-xl transition-colors`}
					>
						Cancelar
					</button>
					<button
						onClick={onConfirm}
						className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
					>
						Confirmar
					</button>
				</div>
			</div>
		</div>
	);
}
